
import { defineComponent, reactive, ref, Ref, UnwrapRef } from "vue";
import { contractResponse, getContract, kei02p } from "@/api/contract";
import HyphenParagraph from "common/src/components/HyphenParagraph.vue";
import constants from "common/src/constants";
import { getGenderText, getHaraikomiText } from "common/src/utils/codes";
import SubInsured from "common/src/components/SubInsured.vue";
import { commaSplit } from "common/src/utils/number";
import { contractChangeRequest, postContractChange } from "@/api/contractChange";
import useVuelidate from "@vuelidate/core";
import { SCR120FormValidateRule } from "@/forms/scr120";
import { addressRequest, getAddress } from "common/src/api/address";
import router from "@/router";
import axios from "axios";
import { setErrorMessage } from "common/src/utils/validate";
import { ErrorResponse } from "common/src/utils/request";
import { scrollToError } from "common/src/utils/screen";
import { getTerms } from "@/api/terms";
import { getSyoyuText, getYoutoText, getKozoText } from "common/src/utils/codes";
import { getFileNameFromContentDisposition } from "common/src/utils/file";
import { getInsurancePolicy } from "@/api/insurancePolicy";

// 仕様変更によりもともとこの画面に実装していた契約内容変更機能がなくなったため、
// scriptの多くは現時点では利用されることが無い
export default defineComponent({
  components: {
    HyphenParagraph,
    SubInsured,
  },
  setup() {
    const contract: UnwrapRef<contractResponse> = reactive({
      kei01p: undefined,
      hsm03p: undefined,
      kei02p: undefined,
    });
    const mainInsured: Ref<kei02p | undefined> = ref(undefined);
    const subInsureds: Ref<kei02p[] | undefined> = ref(undefined);
    const enablePrintYakkan = ref(true);
    const enablePrintSyoken = ref(true);

    const form: UnwrapRef<contractChangeRequest> = reactive({
      kei01p: {
        tfKekyyn: "",
        tfKekya1: "",
        tfKekya2: "",
        tfKekya3: "",
        tfKekytl: "",
        tfKekyt2: "",
        tfKehkdt: 0,
      },
    });

    // 契約情報取得
    getContract()
      .then((res) => {
        contract.kei01p = res.data.kei01p;
        contract.hsm03p = res.data.hsm03p;
        contract.kei02p = res.data.kei02p;

        mainInsured.value = res.data.kei02p?.find((i) => i.tf_khhkno === constants.MAIN_INSURED_NUMBER);
        subInsureds.value = res.data.kei02p
          ?.filter((i) => i.tf_khhkno !== constants.MAIN_INSURED_NUMBER)
          .sort((a, b) => Number.parseInt(a.tf_khhkno) - Number.parseInt(b.tf_khhkno));

        form.kei01p.tfKekyyn = res.data.kei01p?.tf_kekyyn;
        form.kei01p.tfKekya1 = res.data.kei01p?.tf_kekya1;
        form.kei01p.tfKekya2 = res.data.kei01p?.tf_kekya2;
        form.kei01p.tfKekya3 = res.data.kei01p?.tf_kekya3;
        form.kei01p.tfKekytl = res.data.kei01p?.tf_kekytl;
        form.kei01p.tfKekyt2 = res.data.kei01p?.tf_kekyt2;
        form.kei01p.tfKehkdt = res.data.kei01p?.tf_kehkdt;

        enablePrintYakkan.value = res.data.hsm03p?.tf_hrsknf === constants.SYOUKEN_HAKKO.ENABLE;
        enablePrintSyoken.value =
          res.data.hsm03p?.tf_hrsknf === constants.SYOUKEN_HAKKO.ENABLE &&
          res.data.kei01p?.tf_kemskr === constants.MOUSHIKOMI_KEIRO.WEB &&
          res.data.kei01p?.tf_keybd1?.length > 0;
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          router.push({ name: "404" });
        }
      });

    // 全体エラーメッセージ
    const g$: string[] = reactive([]);
    // フィールドエラーを外部からセットする変数
    const $externalResults = ref({
      kei01p: {},
    });
    // フィールドエラー設定初期化
    const f$ = useVuelidate(SCR120FormValidateRule, form, {
      $autoDirty: true,
      $externalResults,
    });

    // 電話番号入力時の検証処理
    const validTelNo = () => {
      $externalResults.value = {
        ...$externalResults.value,
        ...{ kei01p: { tfKekytl: [], tfKekyt2: [] } },
      };
      f$.value.kei01p.tfKekytl.$touch();
      f$.value.kei01p.tfKekyt2.$touch();
      if (!form.kei01p.tfKekytl && !form.kei01p.tfKekyt2) {
        $externalResults.value.kei01p = {
          ...$externalResults.value.kei01p,
          ...{ tfKekytl: ["いずれかの電話番号を入力してください"], tfKekyt2: ["いずれかの電話番号を入力してください"] },
        };
      }
    };

    // 郵便番号入力時の住所取得処理を実装
    const getContractorAddress = async () => {
      if (form.kei01p.tfKekyyn?.length === 7) {
        const req: addressRequest = { zipCode: form.kei01p.tfKekyyn };
        try {
          const res = await (await getAddress(req)).data;
          form.kei01p.tfKekya1 = res.tf_ybthkj + res.tf_ybsckj + res.tf_ybcikj;
          form.kei01p.tfKekya3 = res.tf_ybthkn + res.tf_ybsckn + res.tf_ybcikn;
        } catch (err) {
          return;
        }
      }
    };

    // web保険証券出力処理
    const downloadInsurancePolicy = async () => {
      try {
        const res = await getInsurancePolicy();
        const file = new Blob([res.data]);
        const url = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        const name = getFileNameFromContentDisposition(res.headers["content-disposition"], "insurancePolicy.pdf");
        link.setAttribute("download", name);
        document.body.append(link);
        link.click();
        link.remove();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          router.push({ name: "404" });
        }
      }
    };

    // 約款出力処理
    const downloadTerms = async () => {
      try {
        const res = await getTerms();
        const file = new Blob([res.data]);
        const url = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        const name = getFileNameFromContentDisposition(res.headers["content-disposition"], "terms.pdf");
        link.setAttribute("download", name);
        document.body.append(link);
        link.click();
        link.remove();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          router.push({ name: "404" });
        }
      }
    };

    // 契約情報変更処理呼び出し
    const submit = async () => {
      g$.splice(0, g$.length);
      f$.value.$clearExternalResults();
      validTelNo();
      f$.value.$touch();
      if (f$.value.$invalid) {
        scrollToError();
        return;
      }

      try {
        await postContractChange(form);

        router.push({ name: "ContractChanged" });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          const extError = ref({});
          setErrorMessage(g$, extError, err.response?.data as ErrorResponse);
          $externalResults.value = {
            ...$externalResults.value,
            ...extError.value,
          };
          scrollToError();
        }
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          g$.push("すでにデータが変更されています。画面を更新後、再度実行してください");
        }
      }
    };

    return {
      form,
      contract,
      constants,
      mainInsured,
      subInsureds,
      enablePrintYakkan,
      enablePrintSyoken,
      getGenderText,
      getHaraikomiText,
      commaSplit,
      f$,
      g$,
      validTelNo,
      getContractorAddress,
      downloadTerms,
      submit,
      getSyoyuText,
      getYoutoText,
      getKozoText,
      downloadInsurancePolicy,
    };
  },
});
